import { useSyncExternalStore } from "react";
import {
  BROKER_VERIFICATION_DOC,
  DocSync,
  defaultDeviceName,
  parseVerification,
  readPlatformEnv,
  withVerification,
  type BrokerVerificationDoc,
  type FigureKind,
  type VerificationEntry,
} from "@helm/core";
import { loadStoredDevice } from "../auth/deviceStore";

/**
 * The owner's R|Trader Pro matches (ADR-0045): one synced document per account,
 * `broker_verification/{accountId}`, server-authoritative. Written only by an explicit owner
 * action in the account details; nothing here verifies anything automatically. A record that
 * fails validation verifies nothing (`parseVerification`).
 */

export type VerificationState = {
  doc: BrokerVerificationDoc | null;
  /** 0 = no document yet. */
  version: number;
  loaded: boolean;
  status: "idle" | "loading" | "saving" | "error";
  message: string | null;
};

const EMPTY: VerificationState = { doc: null, version: 0, loaded: false, status: "idle", message: null };

let byAccount: ReadonlyMap<string, VerificationState> = new Map();
const listeners = new Set<() => void>();
const docs = new DocSync();

function patch(accountId: string, p: Partial<VerificationState>): void {
  const m = new Map(byAccount);
  m.set(accountId, { ...(m.get(accountId) ?? EMPTY), ...p });
  byAccount = m;
  for (const l of listeners) l();
}

export function verificationOf(accountId: string): VerificationState {
  return byAccount.get(accountId) ?? EMPTY;
}

/** Every account's parsed record (null = none / not loaded). */
export function verificationDocs(): ReadonlyMap<string, BrokerVerificationDoc | null> {
  const m = new Map<string, BrokerVerificationDoc | null>();
  for (const [id, s] of byAccount) m.set(id, s.doc);
  return m;
}

const inflight = new Map<string, Promise<void>>();

/** Load (or reload) one account's record. Concurrent calls share one request. */
export function loadVerification(accountId: string): Promise<void> {
  const running = inflight.get(accountId);
  if (running) return running;
  const p = (async () => {
    patch(accountId, { status: "loading", message: null });
    try {
      const doc = await docs.load<Record<string, unknown>>(BROKER_VERIFICATION_DOC, accountId);
      patch(accountId, {
        doc: doc ? parseVerification(accountId, doc.body) : null,
        version: doc?.version ?? 0,
        loaded: true,
        status: "idle",
      });
    } catch (e) {
      // Unknown = unverified: the figure keeps its "unverified" tag.
      patch(accountId, { status: "error", message: e instanceof Error ? e.message : "could not load" });
    } finally {
      inflight.delete(accountId);
    }
  })();
  inflight.set(accountId, p);
  return p;
}

/** Load the records of these accounts once each (the view model calls this as accounts appear). */
export function ensureVerifications(accountIds: readonly string[]): void {
  for (const id of accountIds) {
    const s = byAccount.get(id);
    if (s === undefined) void loadVerification(id);
  }
}

/** Another device saved a record (bus `DocChanged`). */
export function verificationDocChanged(accountId: string, version: number): void {
  const cur = byAccount.get(accountId);
  if (cur?.loaded && version > cur.version) void loadVerification(accountId);
}

export type VerificationDraft = Omit<VerificationEntry, "confirmed_at" | "device_id" | "device_label">;

/**
 * The owner confirms (entry) or withdraws (null) one figure's R|Trader Pro match. Never called
 * without an explicit owner action.
 */
export async function saveVerification(
  accountId: string,
  kind: FigureKind,
  draft: VerificationDraft | null,
): Promise<{ ok: boolean; message: string }> {
  if (!verificationOf(accountId).loaded) await loadVerification(accountId);
  const cur = verificationOf(accountId);
  if (!cur.loaded) return { ok: false, message: `Not saved: ${cur.message ?? "could not load"}` };
  let entry: VerificationEntry | null = null;
  if (draft !== null) {
    const device = await loadStoredDevice().catch(() => null);
    entry = {
      ...draft,
      confirmed_at: new Date().toISOString(),
      device_id: device?.id ?? "",
      device_label: defaultDeviceName(readPlatformEnv()),
    };
  }
  const base = (cur.doc ?? {}) as Record<string, unknown>;
  const next = withVerification(accountId, cur.doc, kind, entry) as unknown as Record<string, unknown>;
  patch(accountId, { status: "saving", message: null });
  try {
    const r = await docs.save(BROKER_VERIFICATION_DOC, accountId, base, cur.version, next);
    if (r.kind === "saved") {
      patch(accountId, {
        doc: parseVerification(accountId, r.doc.body),
        version: r.doc.version,
        status: "idle",
      });
      return { ok: true, message: draft === null ? "Verification withdrawn" : "Match recorded" };
    }
    const server = r.server;
    patch(accountId, {
      doc: parseVerification(accountId, server.body),
      version: server.version,
      status: "idle",
    });
    return { ok: false, message: "Changed on another device — review the current record and confirm again." };
  } catch (e) {
    patch(accountId, { status: "error", message: e instanceof Error ? e.message : "save failed" });
    return { ok: false, message: e instanceof Error ? e.message : "save failed" };
  }
}

export function onVerificationChange(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function useVerification(accountId: string): VerificationState {
  return useSyncExternalStore(onVerificationChange, () => verificationOf(accountId));
}

/** Tests and the dev preview: replace every record. */
export function setVerificationsForTest(m: ReadonlyMap<string, VerificationState>): void {
  byAccount = m;
  for (const l of listeners) l();
}
